import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster, toast } from './components/ui/sonner';
import './i18n/config';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Plus, Building, LogOut } from 'lucide-react';
import apiClient from './utils/apiClient';

// Public pages
import LandingPage from './pages/public/LandingPage';
import PublicPrivacy from './pages/public/PublicPrivacy';
import PublicTerms from './pages/public/PublicTerms';

// Auth pages
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';

import PersonalLayout from './layouts/PersonalLayout';
import Dashboard from './pages/personal/Dashboard';
import AIAssistant from './pages/personal/AIAssistant';
import Onboarding from './pages/personal/onboarding/Onboarding';
import TwinOverview from './pages/personal/twin/TwinOverview';
import TwinProfile from './pages/personal/twin/TwinProfile';
import TwinPreferences from './pages/personal/twin/TwinPreferences';
import TrainingHub from './pages/personal/twin/TrainingHub';
import StyleTraining from './pages/personal/twin/StyleTraining';
import KnowledgeTraining from './pages/personal/twin/KnowledgeTraining';
import VoiceTraining from './pages/personal/twin/VoiceTraining';
import TextTraining from './pages/personal/twin/TextTraining';
import VideoTraining from './pages/personal/twin/VideoTraining';
import TwinSkills from './pages/personal/twin/TwinSkills';
import CustomSkills from './pages/personal/twin/CustomSkills';
import CustomSkillEditor from './pages/personal/twin/CustomSkillEditor';
import LifeMap from './pages/personal/life-map/LifeMap';
import Marketplace from './pages/personal/Marketplace';
import MarketplaceDetail from './pages/personal/MarketplaceDetail';
import Skills from './pages/personal/Skills';
import SkillDetail from './pages/personal/SkillDetail';
import PersonalSettings from './pages/personal/Settings';
import AlignmentDashboard from './pages/personal/admin/AlignmentDashboard';
import IdentityFormation from './pages/personal/identity/IdentityFormation';
import RepresentationWorkspace from './pages/personal/representation/RepresentationWorkspace';
import PilotOnboarding from './pages/personal/pilot/PilotOnboarding';
import PrivacyCenter from './pages/personal/privacy/PrivacyCenter';
import Vault from './pages/personal/vault/Vault';
import DailyReflections from './pages/personal/reflections/DailyReflections';
import PersonalGoals from './pages/personal/goals/PersonalGoals';

import EnterpriseLayout from './layouts/EnterpriseLayout';
import EnterpriseDashboard from './pages/enterprise/Dashboard';
import People from './pages/enterprise/people/People';
import Teams from './pages/enterprise/teams/Teams';
import AccessControl from './pages/enterprise/access/AccessControl';
import AppsTools from './pages/enterprise/apps/AppsTools';
import Settings from './pages/enterprise/settings/Settings';
import GeneralSettings from './pages/enterprise/settings/GeneralSettings';
import SecuritySettings from './pages/enterprise/settings/SecuritySettings';
import AuthSettings from './pages/enterprise/settings/AuthSettings';
import DomainsSettings from './pages/enterprise/settings/DomainsSettings';
import InfrastructureSettings from './pages/enterprise/settings/InfrastructureSettings';
import StorageSettings from './pages/enterprise/settings/StorageSettings';
import WebhooksSettings from './pages/enterprise/settings/WebhooksSettings';
import Billing from './pages/enterprise/billing/Billing';
import Usage from './pages/enterprise/billing/Usage';
import Invoices from './pages/enterprise/billing/Invoices';
import PaymentMethods from './pages/enterprise/billing/PaymentMethods';
import Analytics from './pages/enterprise/analytics/Analytics';

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/privacy" element={<PublicPrivacy />} />
          <Route path="/terms" element={<PublicTerms />} />
          
          {/* Auth routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />

          <Route
            path="/personal/*"
            element={
              <ProtectedRoute requireUserType="PERSONAL">
                <PersonalLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            
            <Route path="twin" element={<TwinOverview />} />
            <Route path="twin/profile" element={<TwinProfile />} />
            <Route path="twin/preferences" element={<TwinPreferences />} />
            <Route path="twin/train" element={<TrainingHub />} />
            
            {/* New intent-first routes */}
            <Route path="twin/train/style" element={<StyleTraining />} />
            <Route path="twin/train/knowledge" element={<KnowledgeTraining />} />
            
            {/* Legacy routes - redirect to intent-first structure */}
            <Route path="twin/train/text" element={<Navigate to="/personal/twin/train/style" replace />} />
            <Route path="twin/train/voice" element={<StyleTraining />} />
            <Route path="twin/train/video" element={<StyleTraining />} />
            
            <Route path="twin/skills" element={<TwinSkills />} />
            <Route path="twin/custom-skills" element={<CustomSkills />} />
            <Route path="twin/custom-skills/new" element={<CustomSkillEditor />} />
            <Route path="twin/custom-skills/edit/:skillPackageId" element={<CustomSkillEditor />} />
            
            <Route path="life-map" element={<LifeMap />} />
            
            <Route path="marketplace" element={<Marketplace />} />
            <Route path="marketplace/:skillPackageId" element={<MarketplaceDetail />} />
            
            <Route path="skills" element={<Skills />} />
            <Route path="skills/:skillId" element={<SkillDetail />} />
            
            <Route path="assistant" element={<AIAssistant />} />
            <Route path="assistant/threads/:threadId" element={<AIAssistant />} />
            
            <Route path="settings" element={<PersonalSettings />} />
            
            {/* Admin Routes */}
            <Route path="admin/alignment" element={<AlignmentDashboard />} />
            
            {/* Identity Formation Route */}
            <Route path="identity-formation" element={<IdentityFormation />} />
            <Route path="identity" element={<IdentityFormation />} />
            <Route path="representation" element={<RepresentationWorkspace />} />
            
            {/* Vault/Knowledge Route */}
            <Route path="vault" element={<Vault />} />
            <Route path="knowledge" element={<Navigate to="/personal/vault" replace />} />
            
            {/* Reflections Route */}
            <Route path="reflections" element={<DailyReflections />} />
            
            {/* Goals Route */}
            <Route path="goals" element={<PersonalGoals />} />
            
            {/* Pilot Routes */}
            <Route path="pilot-onboarding" element={<PilotOnboarding />} />
            <Route path="privacy" element={<PrivacyCenter />} />
            
            <Route index element={<Navigate to="dashboard" replace />} />
          </Route>

          <Route
            path="/enterprise/orgs/:orgId/*"
            element={
              <ProtectedRoute requireUserType="ENTERPRISE">
                <EnterpriseLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<EnterpriseDashboard />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="profile" element={<div className="p-8"><h1 className="text-2xl font-bold">Organization Profile</h1></div>} />
            <Route path="people" element={<People />} />
            <Route path="teams" element={<Teams />} />
            <Route path="access" element={<AccessControl />} />
            <Route path="apps" element={<AppsTools />} />
            
            <Route path="billing" element={<Billing />} />
            <Route path="billing/usage" element={<Usage />} />
            <Route path="billing/invoices" element={<Invoices />} />
            <Route path="billing/payment-methods" element={<PaymentMethods />} />
            
            <Route path="settings" element={<Settings />} />
            <Route path="settings/general" element={<GeneralSettings />} />
            <Route path="settings/security" element={<SecuritySettings />} />
            <Route path="settings/auth" element={<AuthSettings />} />
            <Route path="settings/domains" element={<DomainsSettings />} />
            <Route path="settings/infrastructure" element={<InfrastructureSettings />} />
            <Route path="settings/infrastructure/storage" element={<StorageSettings />} />
            <Route path="settings/webhooks" element={<WebhooksSettings />} />
            
            <Route index element={<Navigate to="dashboard" replace />} />
          </Route>

          <Route
            path="/enterprise/orgs"
            element={
              <ProtectedRoute requireUserType="ENTERPRISE">
                <EnterpriseOrgSelector />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster position="bottom-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

function EnterpriseOrgSelector() {
  const [orgs, setOrgs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showCreateDialog, setShowCreateDialog] = React.useState(false);
  const [formData, setFormData] = React.useState({
    name: '',
    slug: '',
    contactEmail: '',
    description: '',
  });
  const [creating, setCreating] = React.useState(false);
  const { user, logout } = useAuth();

  React.useEffect(() => {
    fetchOrgs();
  }, []);

  const fetchOrgs = async () => {
    try {
      const response = await apiClient.get('/organizations/');
      setOrgs(response.data);
      if (response.data.length === 1) {
        window.location.href = `/enterprise/orgs/${response.data[0].id}/dashboard`;
      }
    } catch (error) {
      console.error('Failed to fetch orgs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await apiClient.post('/organizations/', formData);
      toast.success('Organization created successfully');
      window.location.href = `/enterprise/orgs/${response.data.id}/dashboard`;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setCreating(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (orgs.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 enterprise-selection">
        <div className="border-b border-gray-200 bg-white px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-enterprise-slate">HumaTwin</h1>
            <p className="text-sm text-gray-500">Enterprise</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-center p-8" style={{ minHeight: 'calc(100vh - 80px)' }}>
          <div className="max-w-2xl w-full text-center space-y-6">
            <div className="w-20 h-20 rounded-full bg-enterprise-slate/10 flex items-center justify-center mx-auto">
              <Building className="w-10 h-10 text-enterprise-slate" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-gray-900 mb-2">
                Welcome to HumaTwin Enterprise
              </h1>
              <p className="text-lg text-gray-600">
                Create your first organization to get started
              </p>
            </div>

            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button size="lg" data-testid="create-organization-button">
                  <Plus className="w-5 h-5 mr-2" />
                  Create Organization
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold">Create Organization</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateOrg} className="space-y-5 mt-4">
                  <div>
                    <Label htmlFor="name" className="text-sm font-semibold text-gray-700 mb-2 block">
                      Organization Name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="org-name-input"
                      value={formData.name}
                      onChange={(e) => {
                        const name = e.target.value;
                        setFormData({
                          ...formData,
                          name,
                          slug: generateSlug(name),
                        });
                      }}
                      placeholder="Acme Corporation"
                      required
                      className="text-gray-900 placeholder:text-gray-400 h-11 px-4"
                    />
                  </div>

                  <div>
                    <Label htmlFor="slug" className="text-sm font-semibold text-gray-700 mb-2 block">
                      Organization Slug *
                    </Label>
                    <Input
                      id="slug"
                      data-testid="org-slug-input"
                      value={formData.slug}
                      onChange={(e) =>
                        setFormData({ ...formData, slug: e.target.value })
                      }
                      placeholder="acme-corporation"
                      required
                      className="text-gray-900 placeholder:text-gray-400 font-mono h-11 px-4"
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                      Used in URLs (lowercase, hyphens only)
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="contactEmail" className="text-sm font-semibold text-gray-700 mb-2 block">
                      Contact Email *
                    </Label>
                    <Input
                      id="contactEmail"
                      data-testid="org-email-input"
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) =>
                        setFormData({ ...formData, contactEmail: e.target.value })
                      }
                      placeholder="contact@acme.com"
                      required
                      className="text-gray-900 placeholder:text-gray-400 h-11 px-4"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description" className="text-sm font-semibold text-gray-700 mb-2 block">
                      Description (Optional)
                    </Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      placeholder="Tell us about your organization"
                      rows={3}
                      className="text-gray-900 placeholder:text-gray-400 py-3 px-4 resize-none"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreateDialog(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={creating || !formData.name || !formData.slug || !formData.contactEmail}
                      className="flex-1"
                      data-testid="create-org-submit"
                    >
                      {creating ? 'Creating...' : 'Create Organization'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 enterprise-selection">
      <div className="border-b border-gray-200 bg-white px-8 py-4 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-enterprise-slate">HumaTwin</h1>
          <p className="text-sm text-gray-500">Enterprise</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">{user?.name}</p>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={logout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-center p-8" style={{ minHeight: 'calc(100vh - 80px)' }}>
        <div className="max-w-2xl w-full space-y-6">
          <h1 className="font-heading text-3xl font-bold text-center mb-6">Select Organization</h1>
          <div className="grid gap-4">
            {orgs.map((org) => (
              <a
                key={org.id}
                href={`/enterprise/orgs/${org.id}/dashboard`}
                className="p-6 bg-white rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all"
              >
                <h2 className="font-heading text-xl font-semibold">{org.name}</h2>
                <p className="text-sm text-gray-500 mt-1">{org.contactEmail}</p>
              </a>
            ))}
          </div>
          <div className="text-center">
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(true)}
              data-testid="add-organization-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New Organization
            </Button>
          </div>

          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">Create Organization</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateOrg} className="space-y-5 mt-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-semibold text-gray-700 mb-2 block">
                    Organization Name *
                  </Label>
                  <Input
                    id="name"
                    data-testid="org-name-input"
                    value={formData.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setFormData({
                        ...formData,
                        name,
                        slug: generateSlug(name),
                      });
                    }}
                    placeholder="Acme Corporation"
                    required
                    className="text-gray-900 placeholder:text-gray-400"
                  />
                </div>

                <div>
                  <Label htmlFor="slug" className="text-sm font-semibold text-gray-700 mb-2 block">
                    Organization Slug *
                  </Label>
                  <Input
                    id="slug"
                    data-testid="org-slug-input"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({ ...formData, slug: e.target.value })
                    }
                    placeholder="acme-corporation"
                    required
                    className="text-gray-900 placeholder:text-gray-400 font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1.5">
                    Used in URLs (lowercase, hyphens only)
                  </p>
                </div>

                <div>
                  <Label htmlFor="contactEmail" className="text-sm font-semibold text-gray-700 mb-2 block">
                    Contact Email *
                  </Label>
                  <Input
                    id="contactEmail"
                    data-testid="org-email-input"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) =>
                      setFormData({ ...formData, contactEmail: e.target.value })
                    }
                    placeholder="contact@acme.com"
                    required
                    className="text-gray-900 placeholder:text-gray-400"
                  />
                </div>

                <div>
                  <Label htmlFor="description" className="text-sm font-semibold text-gray-700 mb-2 block">
                    Description (Optional)
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Tell us about your organization"
                    rows={3}
                    className="text-gray-900 placeholder:text-gray-400"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateDialog(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={creating || !formData.name || !formData.slug || !formData.contactEmail}
                    className="flex-1"
                    data-testid="create-org-submit"
                  >
                    {creating ? 'Creating...' : 'Create Organization'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}

export default App;
